import { escapeHtml } from "@/lib/tool-kit/research-workflow/document";

/**
 * A literature review synthesis matrix: one row per source, one column per aspect being
 * compared, and theme tags that cut across both.
 *
 * Everything here is pure: the widget keeps the matrix in state and hands it to these
 * functions to sort, filter, save and export. The exports are the point of the tool —
 * a matrix that cannot leave the browser is a matrix nobody can put in a thesis — so
 * each one is built to survive its destination: CSV that Excel, Numbers, Google Sheets
 * and this tool itself read back identically; a Markdown table for notes and
 * repositories; and an HTML table that Word and Google Docs paste as a real table.
 */

export interface MatrixColumn {
  id: string;
  label: string;
}

export interface MatrixRow {
  id: string;
  author: string;
  year: string;
  title: string;
  tags: string[];
  /** Cell text by column id. */
  cells: Record<string, string>;
}

export interface Matrix {
  columns: MatrixColumn[];
  rows: MatrixRow[];
}

/** The fixed columns' header labels, from the widget's strings. */
export interface MatrixHeaders {
  author: string;
  year: string;
  title: string;
  themes: string;
}

export type SortKey =
  | "manual"
  | "author"
  | "yearNewest"
  | "yearOldest"
  | "title";

const TAG_SEPARATOR = /[,;]/;
const LINE_BREAK = /\r\n|\r|\n/g;
const DIGITS = /\d{4}/;
const CELL_BREAKS = /[\t\r\n]+/g;

/* ----------------------------------------------------------------- editing */

export function emptyRow(id: string): MatrixRow {
  return { id, author: "", year: "", title: "", tags: [], cells: {} };
}

/** Tags typed as "methods, sleep; students" — trimmed, de-duplicated, case kept. */
export function parseTags(text: string): string[] {
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const part of text.split(TAG_SEPARATOR)) {
    const tag = part.trim();
    if (tag && !seen.has(tag.toLowerCase())) {
      seen.add(tag.toLowerCase());
      tags.push(tag);
    }
  }
  return tags;
}

/** Every tag in use, alphabetically, for the filter. */
export function allTags(rows: readonly MatrixRow[], locale = "en"): string[] {
  const seen = new Map<string, string>();
  for (const row of rows) {
    for (const tag of row.tags) {
      if (!seen.has(tag.toLowerCase())) {
        seen.set(tag.toLowerCase(), tag);
      }
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, locale));
}

function yearValue(row: MatrixRow): number | null {
  const match = DIGITS.exec(row.year);
  return match ? Number(match[0]) : null;
}

/** A sorted copy. Rows without a year go last whichever way years run. */
export function sortRows(
  rows: readonly MatrixRow[],
  key: SortKey,
  locale = "en"
): MatrixRow[] {
  const copy = [...rows];
  if (key === "manual") {
    return copy;
  }
  if (key === "yearNewest" || key === "yearOldest") {
    const sign = key === "yearNewest" ? -1 : 1;
    return copy.sort((a, b) => {
      const ya = yearValue(a);
      const yb = yearValue(b);
      if (ya === null || yb === null) {
        return Number(ya === null) - Number(yb === null);
      }
      return sign * (ya - yb);
    });
  }
  const field = key === "author" ? "author" : "title";
  return copy.sort((a, b) => {
    const blankA = a[field].trim() === "";
    const blankB = b[field].trim() === "";
    if (blankA || blankB) {
      return Number(blankA) - Number(blankB);
    }
    return a[field].localeCompare(b[field], locale, { sensitivity: "base" });
  });
}

/** Rows containing `query` anywhere (case-insensitive) and carrying `tag`, if given. */
export function filterRows(
  rows: readonly MatrixRow[],
  query: string,
  tag: string
): MatrixRow[] {
  const needle = query.trim().toLowerCase();
  const wanted = tag.trim().toLowerCase();
  return rows.filter((row) => {
    if (wanted && !row.tags.some((t) => t.toLowerCase() === wanted)) {
      return false;
    }
    if (!needle) {
      return true;
    }
    const haystack = [
      row.author,
      row.year,
      row.title,
      ...row.tags,
      ...Object.values(row.cells),
    ]
      .join("\n")
      .toLowerCase();
    return haystack.includes(needle);
  });
}

/* ------------------------------------------------------------------ tables */

function headerRow(matrix: Matrix, headers: MatrixHeaders): string[] {
  return [
    headers.author,
    headers.year,
    headers.title,
    ...matrix.columns.map((column) => column.label),
    headers.themes,
  ];
}

function bodyRows(matrix: Matrix): string[][] {
  return matrix.rows.map((row) => [
    row.author,
    row.year,
    row.title,
    ...matrix.columns.map((column) => row.cells[column.id] ?? ""),
    row.tags.join("; "),
  ]);
}

/* CSV — RFC 4180: quote a field holding a comma, quote or line break; double quotes. */

const NEEDS_QUOTES = /[",\r\n]/;

function csvField(value: string): string {
  return NEEDS_QUOTES.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/**
 * CSV with CRLF line ends. Pass `bom` for a file meant for Excel, which otherwise
 * guesses the encoding and mangles accented names; the importer strips it again.
 */
export function matrixToCsv(
  matrix: Matrix,
  headers: MatrixHeaders,
  bom = false
): string {
  const lines = [headerRow(matrix, headers), ...bodyRows(matrix)].map((cells) =>
    cells.map(csvField).join(",")
  );
  return `${bom ? "\uFEFF" : ""}${lines.join("\r\n")}\r\n`;
}

/** Tab-separated, for pasting into a spreadsheet; tabs and breaks inside cells become spaces. */
export function matrixToTsv(matrix: Matrix, headers: MatrixHeaders): string {
  const clean = (value: string) => value.replace(CELL_BREAKS, " ");
  return [headerRow(matrix, headers), ...bodyRows(matrix)]
    .map((cells) => cells.map(clean).join("\t"))
    .join("\n");
}

/** A GitHub-flavoured Markdown table; pipes escaped, line breaks as <br>. */
export function matrixToMarkdown(
  matrix: Matrix,
  headers: MatrixHeaders
): string {
  const cell = (value: string) =>
    value.replaceAll("|", "\\|").replace(LINE_BREAK, "<br>").trim();
  const head = headerRow(matrix, headers);
  const lines = [
    `| ${head.map(cell).join(" | ")} |`,
    `| ${head.map(() => "---").join(" | ")} |`,
    ...bodyRows(matrix).map((cells) => `| ${cells.map(cell).join(" | ")} |`),
  ];
  return `${lines.join("\n")}\n`;
}

/** An HTML table that Word and Google Docs paste as a real, bordered table. */
export function matrixToHtml(matrix: Matrix, headers: MatrixHeaders): string {
  const cell = (tag: "th" | "td", value: string) =>
    `<${tag} style="border:1px solid #999;padding:4px 6px;vertical-align:top;text-align:left">${escapeHtml(value).replace(LINE_BREAK, "<br>")}</${tag}>`;
  const head = headerRow(matrix, headers)
    .map((value) => cell("th", value))
    .join("");
  const body = bodyRows(matrix)
    .map(
      (cells) => `<tr>${cells.map((value) => cell("td", value)).join("")}</tr>`
    )
    .join("");
  return `<table style="border-collapse:collapse"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

/* -------------------------------------------------------------- CSV import */

/** The delimiter used most in the first line, outside quotes: comma, semicolon or tab. */
export function detectDelimiter(text: string): string {
  const counts: Record<string, number> = { ",": 0, ";": 0, "\t": 0 };
  let quoted = false;
  for (const char of text) {
    if (char === '"') {
      quoted = !quoted;
    } else if (!quoted && (char === "\n" || char === "\r")) {
      break;
    } else if (!quoted && char in counts) {
      counts[char] += 1;
    }
  }
  const [best] = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return best && best[1] > 0 ? best[0] : ",";
}

interface CsvState {
  rows: string[][];
  row: string[];
  field: string;
  quoted: boolean;
}

function endField(state: CsvState): void {
  state.row.push(state.field);
  state.field = "";
}

function endRow(state: CsvState): void {
  endField(state);
  state.rows.push(state.row);
  state.row = [];
}

/** One character inside quotes; returns the index of the last character consumed. */
function quotedStep(state: CsvState, source: string, i: number): number {
  const char = source[i];
  if (char !== '"') {
    state.field += char;
    return i;
  }
  if (source[i + 1] === '"') {
    state.field += '"';
    return i + 1;
  }
  state.quoted = false;
  return i;
}

/** One character outside quotes; returns the index of the last character consumed. */
function plainStep(
  state: CsvState,
  source: string,
  i: number,
  delimiter: string
): number {
  const char = source[i];
  if (char === '"' && state.field === "") {
    state.quoted = true;
  } else if (char === delimiter) {
    endField(state);
  } else if (char === "\n" || char === "\r") {
    endRow(state);
    return char === "\r" && source[i + 1] === "\n" ? i + 1 : i;
  } else {
    state.field += char;
  }
  return i;
}

/**
 * RFC 4180 parsing: quoted fields may hold the delimiter, doubled quotes and line
 * breaks. A leading byte-order mark is ignored and blank lines are dropped.
 */
export function parseCsv(
  text: string,
  delimiter = detectDelimiter(text)
): string[][] {
  const source = text.startsWith("\uFEFF") ? text.slice(1) : text;
  const state: CsvState = { rows: [], row: [], field: "", quoted: false };
  let i = 0;
  while (i < source.length) {
    i =
      (state.quoted
        ? quotedStep(state, source, i)
        : plainStep(state, source, i, delimiter)) + 1;
  }
  if (state.field !== "" || state.row.length > 0) {
    endRow(state);
  }
  return state.rows.filter((cells) => cells.some((cell) => cell.trim() !== ""));
}

type FixedField = "author" | "year" | "title" | "themes";

/** What an imported header becomes: one of the fixed fields, or a column. */
type HeaderRole =
  | { kind: "fixed"; field: FixedField }
  | { kind: "column"; column: MatrixColumn };

/** Header words the importer recognises, besides the page's own labels. */
const FIXED_ALIASES: Record<FixedField, readonly string[]> = {
  author: ["author", "authors", "author(s)", "creator", "creators"],
  year: ["year", "date", "publication year", "pub year"],
  title: ["title", "article title", "source title"],
  themes: ["themes", "theme", "tags", "tag", "keywords"],
};

function fixedFieldOf(
  header: string,
  headers: MatrixHeaders
): FixedField | null {
  const name = header.trim().toLowerCase();
  for (const field of ["author", "year", "title", "themes"] as const) {
    if (
      name === headers[field].trim().toLowerCase() ||
      FIXED_ALIASES[field].includes(name)
    ) {
      return field;
    }
  }
  return null;
}

/**
 * A matrix from CSV text. The first row is the header: Author, Year, Title and
 * Themes (in the page's language or in English) fill the fixed fields, and every other
 * header becomes a column. `makeId` supplies fresh ids.
 */
export function matrixFromCsv(
  text: string,
  headers: MatrixHeaders,
  makeId: (kind: "row" | "column") => string
): Matrix | null {
  const [head, ...body] = parseCsv(text);
  if (!head || head.length === 0) {
    return null;
  }
  const columns: MatrixColumn[] = [];
  const roles = head.map((name): HeaderRole => {
    const field = fixedFieldOf(name, headers);
    if (field) {
      return { kind: "fixed", field };
    }
    const column = { id: makeId("column"), label: name.trim() };
    columns.push(column);
    return { kind: "column", column };
  });
  const rows = body.map((cells) => {
    const row = emptyRow(makeId("row"));
    for (const [index, role] of roles.entries()) {
      const value = (cells[index] ?? "").trim();
      if (role.kind === "column") {
        row.cells[role.column.id] = value;
      } else if (role.field === "themes") {
        row.tags = parseTags(value);
      } else {
        row[role.field] = value;
      }
    }
    return row;
  });
  return { columns, rows };
}

/* ------------------------------------------------------------------ storage */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function readColumn(raw: unknown): MatrixColumn | null {
  if (!isRecord(raw) || typeof raw.id !== "string" || raw.id === "") {
    return null;
  }
  return { id: raw.id, label: asString(raw.label) };
}

function readRow(raw: unknown, columnIds: Set<string>): MatrixRow | null {
  if (!isRecord(raw) || typeof raw.id !== "string" || raw.id === "") {
    return null;
  }
  const cells: Record<string, string> = {};
  if (isRecord(raw.cells)) {
    for (const [key, value] of Object.entries(raw.cells)) {
      if (columnIds.has(key) && typeof value === "string") {
        cells[key] = value;
      }
    }
  }
  return {
    id: raw.id,
    author: asString(raw.author),
    year: asString(raw.year),
    title: asString(raw.title),
    tags: Array.isArray(raw.tags)
      ? raw.tags.filter((tag): tag is string => typeof tag === "string")
      : [],
    cells,
  };
}

/**
 * A saved matrix, validated field by field. Anything malformed — another tool's data
 * under the same key, a hand-edited value, an older format — is dropped rather than
 * trusted, and an unreadable save returns null so the widget starts fresh.
 */
export function readSavedMatrix(json: string | null): Matrix | null {
  if (!json) {
    return null;
  }
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (
    !(isRecord(raw) && Array.isArray(raw.columns) && Array.isArray(raw.rows))
  ) {
    return null;
  }
  const columns = raw.columns
    .map(readColumn)
    .filter((column): column is MatrixColumn => column !== null);
  const ids = new Set(columns.map((column) => column.id));
  const rows = raw.rows
    .map((row) => readRow(row, ids))
    .filter((row): row is MatrixRow => row !== null);
  return { columns, rows };
}

/* ------------------------------------------------------------------- merging */

/** True when no source has any text — the state a fresh matrix starts in. */
export function isBlankMatrix(matrix: Matrix): boolean {
  return matrix.rows.every(
    (row) =>
      row.author.trim() === "" &&
      row.year.trim() === "" &&
      row.title.trim() === "" &&
      row.tags.length === 0 &&
      Object.values(row.cells).every((value) => value.trim() === "")
  );
}

/**
 * Add imported sources to a matrix. Columns are matched by name, ignoring case, so a
 * CSV exported earlier lines up with the columns it came from; new names become new
 * columns. A blank matrix is simply replaced, keeping the imported column order.
 */
export function mergeMatrices(base: Matrix, incoming: Matrix): Matrix {
  if (isBlankMatrix(base)) {
    return {
      columns: incoming.columns.length > 0 ? incoming.columns : base.columns,
      rows: incoming.rows,
    };
  }
  const columns = [...base.columns];
  const idFor = new Map<string, string>();
  for (const column of incoming.columns) {
    const match = columns.find(
      (existing) => existing.label.toLowerCase() === column.label.toLowerCase()
    );
    if (match) {
      idFor.set(column.id, match.id);
    } else {
      columns.push(column);
      idFor.set(column.id, column.id);
    }
  }
  const rows = incoming.rows.map((row) => ({
    ...row,
    cells: Object.fromEntries(
      Object.entries(row.cells).map(([id, value]) => [
        idFor.get(id) ?? id,
        value,
      ])
    ),
  }));
  return { columns, rows: [...base.rows, ...rows] };
}
